import { useEffect, useState, useCallback } from "react";
import Iframe from "../../../components/Iframe";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { MESSAGE_TYPES } from "../../../utils/editUtils";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import {
  executeValidationWithArrays,
  handleResetWithValidation,
} from "../../../utils/formValidation";

// パンくず
const breadcrumbs = [
  { title: "Home", path: `/` },
  { title: "Review", path: `/review` },
];

export default function ReviewEditPage() {
  const contentKey = "studioContent"; // ページのキー
  const [reviews, setReviews] = useState([]);
  const [originalSlug, setOriginalSlug] = useState(""); // Iframe表示用の元のslug
  const [message, setMessage] = useState(""); // メッセージ
  const [loadingText, setLoadingText] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  // messageが文字列の場合、3秒後に自動で消去
  useEffect(() => {
    if (message && message.trim() !== "") {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [message]);

  // スタジオリストの取得
  const fetchStudiosList = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_studios/`, {
        method: "GET",
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setOriginalSlug(
        Array.isArray(data) && data.length > 0 ? data[0].slug : ""
      );
    } catch (error) {
      console.error("スタジオリストの取得に失敗しました", error);
    }
  }, []);

  // 口コミデータの取得
  const fetchReviewsData = useCallback(async () => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_reviews/`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      // dataが配列でない場合は空配列を設定
      const reviewsArray = Array.isArray(data) ? data : [];
      setReviews(reviewsArray);
      setHasChanges(false);

      // iframeにも通知して初期データを設定
      setTimeout(() => {
        const iframe = document.querySelector("iframe");
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.TEXT_UPDATE,
              contentKey,
              studioContent: reviewsArray,
              // allData: { studioContent: reviewsArray },
            },
            process.env.REACT_APP_ORIGIN
          );
        }
      }, 100);
    } catch (error) {
      console.error("詳細の取得に失敗しました", error);
    }
    setLoadingText("");
  }, [contentKey]);

  // 初回のみ実行（スタジオリストと口コミデータの取得）
  useEffect(() => {
    fetchStudiosList();
    fetchReviewsData();
  }, [fetchStudiosList, fetchReviewsData]);

  // 口コミデータを保存（新規作成・更新）
  const handleReviewDataSubmit = async () => {
    // 保存するデータ
    const formData = {
      reviews: reviews,
    };

    // バリデーション実行
    const allFormData = {
      reviews: reviews,
    };

    if (
      !executeValidationWithArrays(
        allFormData,
        validationRules,
        arrayValidationRules,
        fieldLabels,
        setMessage
      )
    ) {
      return;
    }

    try {
      setLoadingText("保存中");

      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/save_review/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(`${data.message} status: ${response.status}`);
      }

      setMessage(data.message);
      setHasChanges(false);

      // 更新の場合は最新データを取得
      await fetchReviewsData();
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
    }
    setLoadingText("");
  };

  // バリデーションルールを定義
  const validationRules = {};

  // 配列要素のバリデーションルール
  const arrayValidationRules = {
    reviews: {
      reviewer_info: { required: true },
      content: { required: true },
      reply: { required: true },
    },
  };

  // フィールドラベルのマッピング
  const fieldLabels = {
    reviewer_info: "投稿者名",
    content: "口コミ内容",
    reply: "返信内容",
  };

  // フォーカス時の処理（スクロール＋プレビュー更新）
  const handleInputFocus = (e) => {
    // iframeのReview部分までスクロール
    setTimeout(() => {
      const iframe = document.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: "SCROLL_TO_REVIEW",
          },
          process.env.REACT_APP_ORIGIN
        );
      }
    }, 50);

    // プレビューも更新
    handleInputChange(e);
  };

  // テキスト入力・チェックボックスハンドラー
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const index = parseInt(e.target.dataset.index);

    // エラークリア処理
    if (
      e.target &&
      e.target.classList &&
      e.target.classList.contains("validation-error")
    ) {
      e.target.classList.remove("validation-error");

      const wrapper = e.target.closest(".form-field-wrapper");
      if (wrapper) {
        const errorLabel = wrapper.querySelector(".validation-error-label");
        if (errorLabel) {
          errorLabel.remove();
        }
      }
    }

    // name属性から実際のフィールド名を抽出（reviews0_reviewer_info -> reviewer_info）
    const fieldName = name.startsWith("reviews")
      ? name.split("_").slice(1).join("_")
      : name;

    // チェックボックスの場合はchecked値、それ以外はvalue値を使用
    const fieldValue = type === "checkbox" ? checked : value;

    setReviews((prev) => {
      const newReviews = [...prev];
      newReviews[index] = {
        ...newReviews[index],
        [fieldName]: fieldValue,
      };

      // iframe側に更新を通知
      setTimeout(() => {
        const iframe = document.querySelector("iframe");
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.TEXT_UPDATE,
              contentKey,
              studioContent: newReviews,
              // allData: { studioContent: newReviews },
            },
            process.env.REACT_APP_ORIGIN
          );
        }
      }, 50);

      return newReviews;
    });

    setHasChanges(true);
  };

  // 削除ボタンのハンドラー
  const handleDelete = (index, review) => {
    // UIから削除
    setReviews((prev) => {
      const newReviews = [...prev];
      newReviews.splice(index, 1);

      // iframe側に削除メッセージを送信（更新後のデータを使用）
      setTimeout(() => {
        const iframe = document.querySelector("iframe");
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.TEXT_UPDATE,
              contentKey,
              studioContent: newReviews,
              // allData: { studioContent: newReviews },
            },
            process.env.REACT_APP_ORIGIN
          );
        }
      }, 50);

      return newReviews;
    });

    setHasChanges(true);
  };


  // 追加ボタンのハンドラー
  const handleAdd = (index) => {
    // 新しいレビューオブジェクトを作成
    const newReview = {
      id: -Date.now(), // 一時的な負のIDを生成
      reviewer_info: "",
      content: "",
      reply: "",
      is_published: false, // デフォルトは非公開
    };

    // UIを更新
    setReviews((prev) => {
      const newReviews = [...prev];
      newReviews.splice(index + 1, 0, newReview);

      // iframe側に更新メッセージを送信
      setTimeout(() => {
        const iframe = document.querySelector("iframe");
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.TEXT_UPDATE,
              contentKey,
              studioContent: newReviews,
              // allData: { studioContent: newReviews },
            },
            process.env.REACT_APP_ORIGIN
          );
        }
      }, 50);

      return newReviews;
    });

    setHasChanges(true);
  };

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            {/* パンくず */}
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              onResizeStart={onResizeStart}
            />

            <div className={`message ${message ? "success" : ""}`}>
              <p>{message}</p>
            </div>

            {/* 編集フォーム */}
            <div className="form">
              <div className="area2">
                {reviews &&
                  Array.isArray(reviews) &&
                  reviews.map((review, index) => (
                    <section key={review.id}>
                      <div className="item">
                        <div className="ctrl">
                          <p>{index + 1}</p>
                          <div className="icon-wrap">
                            <i
                              className="icon delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(index, review);
                              }}
                            ></i>
                            <i
                              className="icon add"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAdd(index);
                              }}
                            ></i>
                          </div>
                        </div>
                        <div
                          className="forms"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="text"
                            name={`reviews${index}_reviewer_info`}
                            value={review.reviewer_info || ""}
                            placeholder="投稿者名を入力"
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            data-label="投稿者名"
                            data-required="true"
                            data-index={index}
                          />

                          <textarea
                            name={`reviews${index}_content`}
                            value={review.content || ""}
                            placeholder="口コミ内容を入力"
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            data-label="口コミ内容"
                            data-required="true"
                            data-index={index}
                          />

                          <textarea
                            name={`reviews${index}_reply`}
                            value={review.reply || ""}
                            placeholder="返信内容を入力"
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            data-label="返信内容"
                            data-required="true"
                            data-index={index}
                          />
                          <label>
                            <input
                              type="checkbox"
                              name={`reviews${index}_is_published`}
                              checked={review.is_published || false}
                              onChange={handleInputChange}
                              data-index={index}
                            />
                            <span>公開する</span>
                          </label>
                        </div>
                      </div>
                    </section>
                  ))}
              </div>
            </div>
            {/* 送信ボタン */}
            <div className="submit-area">
              <button
                type="reset"
                onClick={() => {
                  handleResetWithValidation(fetchReviewsData);
                }}
                disabled={!hasChanges}
              >
                Reset
              </button>
              <button
                type="submit"
                onClick={handleReviewDataSubmit}
                disabled={!hasChanges}
              >
                Submit
              </button>
            </div>
          </>
        )}
      </ResizableEditArea>
      {/* ローディング */}
      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}
      {/* プレビュー */}
      <Iframe
        path={`/studio/${originalSlug}`}
        data={reviews}
        contentKey={contentKey}
      />
    </>
  );
}
